/**
 * WorkspaceForm Component
 *
 * Modal form for editing workspace details.
 *
 * Requirements:
 * - FR-016: Allow admins to configure workspace settings
 *
 * Conformed to the Spec 197 design blueprint: uses the canonical kit <Modal>
 * (transparent-but-blocking backdrop, framed, movable, no outside-close) and
 * design tokens instead of the hand-rolled opaque-backdrop dialog.
 */

import React, { useState, useEffect } from 'react';
import { Workspace } from '../../pages/Workspaces';
import api from '../../services/api';
import { Modal, Button } from '../../../../components/ui';

interface WorkspaceFormProps {
  workspace: Workspace | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Environment options
 */
const environments = ['Development', 'Test', 'Staging', 'Production'];

/**
 * WorkspaceForm Component
 */
const WorkspaceForm: React.FC<WorkspaceFormProps> = ({ workspace, onClose, onSaved }) => {
  // Form state
  const [name, setName] = useState(workspace?.name || '');
  const [description, setDescription] = useState(workspace?.description || '');
  // Spec 216: `environment` was a fabricated field (the API hardcoded it);
  // it no longer exists on Workspace — the form keeps a local default only.
  const [environment, setEnvironment] = useState('Development');
  const [loading, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form when workspace changes
  useEffect(() => {
    if (workspace) {
      setName(workspace.name);
      setDescription(workspace.description || '');
    }
  }, [workspace]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!workspace) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await api.put(`/api/v1/workspaces/${workspace.id}`, {
        name,
        description: description || null,
        environment,
      });

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save workspace');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={workspace ? 'Edit Workspace' : 'Create Workspace'}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="workspace-form" loading={loading} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="workspace-form" onSubmit={handleSubmit}>
        {/* Error Alert */}
        {error && (
          <div className='mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4'>
            <div className='flex'>
              <div className='flex-shrink-0'>
                <svg
                  className='h-5 w-5 text-red-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <p className='text-sm font-medium text-red-800 dark:text-red-300'>{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className='space-y-4'>
          {/* Name Field */}
          <div>
            <label htmlFor='name' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
              Name
            </label>
            <input
              type='text'
              id='name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className='mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] px-3 py-2 shadow-sm focus:border-[var(--color-accent)] focus:outline-none focus:ring-[var(--color-accent)] sm:text-sm'
              placeholder='Workspace name'
            />
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor='description' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
              Description
            </label>
            <textarea
              id='description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className='mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] px-3 py-2 shadow-sm focus:border-[var(--color-accent)] focus:outline-none focus:ring-[var(--color-accent)] sm:text-sm'
              placeholder='Optional description'
            />
          </div>

          {/* Environment Field */}
          <div>
            <label htmlFor='environment' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
              Environment
            </label>
            <select
              id='environment'
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              className='mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] px-3 py-2 shadow-sm focus:border-[var(--color-accent)] focus:outline-none focus:ring-[var(--color-accent)] sm:text-sm'
            >
              {environments.map((env) => (
                <option key={env} value={env}>
                  {env}
                </option>
              ))}
            </select>
          </div>

          {/* Fabric Workspace ID (read-only) */}
          {workspace && (
            <div>
              <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>
                Fabric Workspace ID
              </label>
              <div className='mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-text-secondary)]'>
                {workspace.fabric_workspace_id}
              </div>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default WorkspaceForm;
